import React, { useState, FC, Children, CSSProperties, useRef, useCallback, useEffect } from 'react';
import SwiperType, { Autoplay, Navigation, Pagination, Thumbs } from 'swiper';
import { Swiper, SwiperProps, SwiperSlide } from 'swiper/react'; // eslint-disable-line import/no-unresolved
import 'swiper/css'; // eslint-disable-line import/no-unresolved
import 'swiper/css/navigation'; // eslint-disable-line import/no-unresolved
import 'swiper/css/pagination'; // eslint-disable-line import/no-unresolved
import 'swiper/css/scrollbar'; // eslint-disable-line import/no-unresolved
import 'swiper/css/autoplay'; // eslint-disable-line import/no-unresolved
import { NavigationOptions } from 'swiper/types';
import useClassNames from '@/helpers/hooks/useClassNames';
import useTouchDevice from '@/hooks/useTouchDevice';
import SwiperCore from 'swiper';
import SliderNavigation, { SliderNavigationProps } from './slider-navigation';

export type SliderProps = SliderNavigationProps & {
  allowArrowsOnTouchDevice?: boolean;
  className?: string;
  containerClassName?: string;
  spaceBetween?: number;
  slidesPerView?: number;
  dots?: boolean;
  fractionPagination?: boolean;
  fitToSlides?: boolean;
  slideWidth?: number;
  withThumbs?: boolean;
  slideWidthIsFlexible?: boolean;
  onSliderInitiated?: VoidFunction;
} & SwiperProps;

const Slider: FC<SliderProps> = ({
  className = '',
  containerClassName = '',
  slideWidth = 300,
  slidesPerView,
  fitToSlides = false,
  arrows = false,
  innerArrows = false,
  solidArrows = false,
  dots = true,
  fractionPagination = false,
  spaceBetween = 20,
  withThumbs = false,
  children,
  onSwiper,
  onInit,
  allowTouchMove,
  prevButtonStyles = {},
  nextButtonStyles = {},
  prevArrowStyles = {},
  nextArrowStyles = {},
  compactNavigation,
  slideWidthIsFlexible,
  allowArrowsOnTouchDevice = false,
  autoplay,
  onSliderInitiated = () => {},
  ...props
}) => {
  const { isTouchDevice } = useTouchDevice();

  const [init, setInit] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isTouched, setIsTouched] = useState(false);

  const handleInit = useCallback(
    (swiper: SwiperType) => {
      setTimeout(() => {
        setInit(true);
        onSliderInitiated();
      });
      onInit?.(swiper);
    },
    [onInit],
  );

  SwiperCore.use([Autoplay]);

  const navigationPrevRef = useRef<HTMLDivElement>(null);
  const navigationNextRef = useRef<HTMLDivElement>(null);

  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType>();

  const swiperRef = useRef<SwiperType | null>(null);

  const validToFit: boolean = !!fitToSlides && !!slideWidth && !!slidesPerView;
  const sliderWidth: CSSProperties['width'] =
    validToFit && slidesPerView ? `${spaceBetween * (slidesPerView - 1) + slideWidth * slidesPerView}px` : '100%';

  const containerClassNames = useClassNames([
    containerClassName,
    'slider_container relative',
    !init ? 'hidden' : 'block',
    {
      'slider__container--fit': validToFit,
      'slider__container--with-thumbs': withThumbs,
    },
  ]);
  const slidesClassName = useClassNames(['slider', className]);

  const slideProps = {
    width: slideWidthIsFlexible ? 'fit-content' : `${slideWidth}px`,
  } as React.CSSProperties;

  const slides = Children.map(children, (child) => <SwiperSlide style={slideProps}>{child}</SwiperSlide>);

  const handleOnSwiper = (swiper: SwiperType) => {
    swiperRef.current = swiper;
    onSwiper?.(swiper);

    if (withThumbs) {
      setThumbsSwiper(swiper);
    }
  };

  const handleOnBeforeInit = (swiper: SwiperType) => {
    (swiper.params.navigation as NavigationOptions).prevEl = navigationPrevRef.current;
    (swiper.params.navigation as NavigationOptions).nextEl = navigationNextRef.current;
  };

  const handleTouchStart = () => {
    setIsTouched(true);
    console.log('Autoplay paused due to touch start.');
    swiperRef.current?.autoplay?.stop();
  };

  const handleTouchEnd = () => {
    setIsTouched(false);
    console.log('Autoplay resumed due to touch end.');
    swiperRef.current?.autoplay?.start();
  };

  useEffect(() => {
    const swiperInstance = swiperRef.current;
    if (swiperInstance) {
      if (swiperInstance.autoplay) {
        if (autoplay === false || isHovered || isTouched) {
          swiperInstance.autoplay.stop();
        } else {
          swiperInstance.autoplay.start();
        }
      }
    }

    return () => {
      if (swiperInstance && swiperInstance.autoplay) {
        swiperInstance.autoplay.stop();
      }
    };
  }, [init, autoplay, isHovered, isTouched]);

  return (
    <div
      className={containerClassNames}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <Swiper
        key={`${allowTouchMove} ${isTouchDevice}`}
        className={slidesClassName}
        modules={[Navigation, Pagination, Thumbs]}
        thumbs={{ swiper: thumbsSwiper }}
        pagination={
          dots
            ? { clickable: true, bulletActiveClass: 'slider__bullet--active' }
            : fractionPagination
            ? {
                clickable: true,
                type: 'fraction',
                renderFraction: function (currentClass: string, totalClass: string) {
                  return (
                    '<span class="' + currentClass + '"></span>' + ' of ' + '<span class="' + totalClass + '"></span>'
                  );
                },
              }
            : false
        }
        slidesPerView={slidesPerView ?? 'auto'}
        spaceBetween={spaceBetween}
        style={{ width: sliderWidth }}
        autoplay={
          autoplay
            ? {
                delay: 5000,
                disableOnInteraction: false,
              }
            : false
        }
        navigation={{
          prevEl: navigationPrevRef.current,
          nextEl: navigationNextRef.current,
        }}
        // loop={true} // Enable looping
        // loopAdditionalSlides={slidesPerView} // Adds extra slides for seamless looping
        onSwiper={handleOnSwiper}
        onBeforeInit={handleOnBeforeInit}
        observer
        observeParents
        onInit={handleInit}
        allowTouchMove={allowTouchMove && isTouchDevice}
        onSlideChange={(swiper: SwiperType) => {
          swiper.realIndex;
        }}
        onReachEnd={(swiper: SwiperType) => {}}
        onReachBeginning={(swiper: SwiperType) => {}}
        {...props}
      >
        {slides}
      </Swiper>
      <SliderNavigation
        compactNavigation={compactNavigation}
        arrows={allowArrowsOnTouchDevice ? arrows : arrows && !isTouchDevice}
        prevButtonStyles={prevButtonStyles}
        nextButtonStyles={nextButtonStyles}
        prevArrowStyles={prevArrowStyles}
        nextArrowStyles={nextArrowStyles}
        navigationPrevRef={navigationPrevRef}
        navigationNextRef={navigationNextRef}
        totalSlides={slides?.length}
        swiperRef={swiperRef.current}
        innerArrows={innerArrows}
        solidArrows={solidArrows}
      />
    </div>
  );
};

export default Slider;
